import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import * as client from "../../../client";
import { DocumentsTab } from "./DocumentsTab";
import { FormSelectionTab } from "./FormSelectionTab";

// Lazy-loaded so superdoc (the docx editor) is split into its own chunk,
// fetched only when the quote tab is opened.
const QuoteTab = React.lazy(() =>
  import("./QuoteTab").then((m) => ({ default: m.QuoteTab }))
);
import { usePolicyApplicationFields, useUpdatePolicyOverlay } from "./data";
import {
  formatCurrency,
  statusLabel,
  statusSeverity,
  sourceBadge,
} from "./util";
import styles from "./PolicyApplicationWorkspace.module.css";

type FieldValue = client.ResolvedField["value"];

type WorkspaceTab = "overview" | "forms" | "quote" | "documents";

const TABS: { key: WorkspaceTab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "forms", label: "Form Selection" },
  { key: "quote", label: "Quote" },
  { key: "documents", label: "Documents" },
];

// Display order for sections, mirroring the backend registry (SECTION_ORDER).
const SECTION_ORDER = [
  "Named Insured",
  "Mailing Info",
  "Policy Dates",
  "Forms",
  "Perils",
  "Premiums",
  "Limits",
  "Sublimits",
  "Deductibles",
  "Added",
];

const SECTION_PREFIX: Record<string, string> = {
  Limits: "limit",
  Sublimits: "sublimit",
  Deductibles: "deductible",
};
const PREFIX_SECTION: Record<string, string> = {
  limit: "Limits",
  sublimit: "Sublimits",
  deductible: "Deductibles",
};

const sectionIndex = (section: string): number => {
  const i = SECTION_ORDER.indexOf(section);
  return i < 0 ? SECTION_ORDER.length : i;
};

const has = (obj: Record<string, FieldValue>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key);

const isBlank = (v: FieldValue): boolean =>
  v === null ||
  v === "" ||
  v === undefined ||
  (typeof v === "number" && Number.isNaN(v));

const fieldValue = (fields: client.ResolvedField[], key: string): FieldValue =>
  fields.find((f) => f.key === key)?.value ?? null;

// Premium-section values arrive as raw numbers (dollar amounts) so math on
// them stays possible; render them as currency here.
const displayValue = (
  field: client.ResolvedField,
  value: FieldValue
): string =>
  field.section === "Premiums" && typeof value === "number"
    ? formatCurrency(value)
    : String(value);

export const PolicyApplicationWorkspace: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { data, isLoading } = usePolicyApplicationFields(applicationId ?? "");
  const update = useUpdatePolicyOverlay(applicationId ?? "");

  const saved = data?.application.working_set.overlay;
  const savedKey = saved ? JSON.stringify(saved) : "";
  const [draft, setDraft] = useState<client.PolicyFieldOverlay | null>(null);
  const [tab, setTab] = useState<WorkspaceTab>("overview");
  const [editingKeys, setEditingKeys] = useState<Set<string>>(new Set());
  const [addingSection, setAddingSection] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("");

  // Reset the draft whenever the server overlay changes (initial load + save).
  // Keyed on savedKey (a JSON snapshot) so a local edit never triggers a reset.
  useEffect(() => {
    if (saved) {
      setDraft({
        upserts: { ...saved.upserts },
        removals: [...saved.removals],
      });
      setEditingKeys(new Set());
    }
  }, [savedKey]);

  const dirty = draft !== null && savedKey !== JSON.stringify(draft);

  const setUpsert = (key: string, value: FieldValue) =>
    setDraft((d) =>
      d
        ? {
            upserts: { ...d.upserts, [key]: value },
            removals: d.removals.filter((k) => k !== key),
          }
        : d
    );

  const revert = (key: string) =>
    setDraft((d) => {
      if (!d) return d;
      const upserts = { ...d.upserts };
      delete upserts[key];
      return { upserts, removals: d.removals.filter((k) => k !== key) };
    });

  const remove = (key: string) =>
    setDraft((d) => {
      if (!d) return d;
      const upserts = { ...d.upserts };
      delete upserts[key];
      return {
        upserts,
        removals: d.removals.includes(key) ? d.removals : [...d.removals, key],
      };
    });

  const toggleEdit = (key: string) =>
    setEditingKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const confirmAdd = (section: string) => {
    const prefix = SECTION_PREFIX[section];
    const name = newName.trim();
    if (!prefix || !name) return;
    setUpsert(`${prefix}:${name}`, newValue);
    setAddingSection(null);
    setNewName("");
    setNewValue("");
  };

  const draftValue = (field: client.ResolvedField): FieldValue => {
    const overridden = draft && has(draft.upserts, field.key);
    const upsertValue = draft?.upserts[field.key];
    return overridden && upsertValue !== undefined ? upsertValue : field.value;
  };

  const groups = useMemo(() => {
    if (!data || !draft) return [];
    const serverKeys = new Set(data.fields.map((f) => f.key));
    const added: client.ResolvedField[] = Object.keys(draft.upserts)
      .filter((key) => !serverKeys.has(key))
      .map((key, index) => {
        const [rawPrefix, ...rest] = key.split(":");
        const prefix = rawPrefix ?? "";
        return {
          key,
          section: PREFIX_SECTION[prefix] ?? "Added",
          order: 1000 + index,
          label: rest.join(":") || key,
          value: draft.upserts[key] ?? null,
          editable: true,
          input_type: "text",
          options: null,
          removable: true,
          required: false,
          source: "added",
        };
      });

    const bySection = new Map<string, client.ResolvedField[]>();
    for (const field of [...data.fields, ...added]) {
      const existing = bySection.get(field.section);
      if (existing) existing.push(field);
      else bySection.set(field.section, [field]);
    }
    for (const section of data.addable_sections) {
      if (!bySection.has(section)) bySection.set(section, []);
    }
    return [...bySection.keys()]
      .sort((a, b) => sectionIndex(a) - sectionIndex(b))
      .map((section) => ({
        section,
        fields: (bySection.get(section) ?? [])
          .slice()
          .sort((x, y) => x.order - y.order),
      }));
  }, [data, draft]);

  if (isLoading || !data || !draft) {
    return (
      <div className={styles.loading}>
        <ProgressSpinner style={{ width: 44, height: 44 }} />
      </div>
    );
  }

  const { application, fields } = data;
  const insured = fieldValue(fields, "insured:named");
  const effective = fieldValue(fields, "dates:effective");
  const expiration = fieldValue(fields, "dates:expiration");
  const title = insured ? String(insured) : "Policy Application";

  const missingRequired = fields.some(
    (f) => f.required && isBlank(draftValue(f))
  );

  const renderEditor = (field: client.ResolvedField, value: FieldValue) => {
    switch (field.input_type) {
      case "select":
        return (
          <Dropdown
            className={styles.input}
            value={value}
            options={(field.options ?? []).map((o) => ({ label: o, value: o }))}
            placeholder="Select…"
            onChange={(e) => setUpsert(field.key, e.value)}
          />
        );
      case "number":
        return (
          <InputNumber
            inputClassName={styles.numberInput}
            value={typeof value === "number" ? value : null}
            useGrouping={false}
            maxFractionDigits={4}
            onValueChange={(e) => setUpsert(field.key, e.value ?? null)}
          />
        );
      default:
        return (
          <InputTextarea
            className={styles.input}
            value={value === null ? "" : String(value)}
            autoResize
            rows={1}
            onChange={(e) => setUpsert(field.key, e.target.value)}
          />
        );
    }
  };

  const renderField = (field: client.ResolvedField) => {
    const overridden = has(draft.upserts, field.key);
    const removed = draft.removals.includes(field.key);
    const value = draftValue(field);
    const source: client.FieldSource = !field.editable
      ? "inherited"
      : removed
        ? "removed"
        : overridden
          ? field.source === "added"
            ? "added"
            : "overridden"
          : "inherited";
    const badge = sourceBadge(source);
    const invalid = field.required && isBlank(value) && !removed;
    const editing = editingKeys.has(field.key);
    const isBoolean = field.input_type === "boolean";
    const canEdit = field.editable && !isBoolean && !removed;
    // Invisible stand-in with a real button's footprint, so the action column
    // holds a constant number of slots and the value width never shifts.
    const spacer = (
      <Button
        icon="pi pi-pencil"
        rounded
        text
        size="small"
        className={styles.spacerBtn}
        tabIndex={-1}
        type="button"
      />
    );

    return (
      <div
        key={field.key}
        className={`${styles.field} ${removed ? styles.removedField : ""} ${
          invalid ? styles.invalid : ""
        }`}
      >
        <div className={styles.label}>
          {field.label}
          {field.required && <span className={styles.req}>*</span>}
        </div>
        <div className={styles.control}>
          {isBoolean ? (
            <InputSwitch
              checked={Boolean(value)}
              disabled={!field.editable}
              onChange={
                field.editable
                  ? (e) => setUpsert(field.key, e.value)
                  : undefined
              }
            />
          ) : !field.editable ? (
            <span className={styles.readonly}>
              {isBlank(value) ? "—" : displayValue(field, value)}
            </span>
          ) : editing ? (
            renderEditor(field, value)
          ) : (
            <span
              className={invalid ? styles.invalidText : styles.viewValue}
              onClick={() => canEdit && toggleEdit(field.key)}
            >
              {isBlank(value) ? (invalid ? "Required" : "—") : String(value)}
            </span>
          )}

          <div className={styles.actions}>
            {/* Slot 1: edit toggle (or a spacer for non-editable rows). Boolean
                fields have no toggle and render neither slot's button. */}
            {!isBoolean &&
              (canEdit ? (
                <Button
                  icon={editing ? "pi pi-check" : "pi pi-pencil"}
                  rounded
                  text
                  size="small"
                  tooltip={editing ? "Done" : "Edit"}
                  onClick={() => toggleEdit(field.key)}
                />
              ) : field.editable ? (
                spacer
              ) : null)}
            {/* Slot 2: revert / remove, else a spacer to keep the width fixed. */}
            {field.editable &&
              (overridden || removed ? (
                <Button
                  icon="pi pi-replay"
                  rounded
                  text
                  size="small"
                  tooltip="Revert to submission"
                  onClick={() => revert(field.key)}
                />
              ) : field.removable && !editing ? (
                <Button
                  icon="pi pi-eye-slash"
                  rounded
                  text
                  size="small"
                  tooltip="Remove from this policy"
                  onClick={() => remove(field.key)}
                />
              ) : (
                spacer
              ))}
          </div>
        </div>
        {badge && (
          <Tag
            value={badge.label}
            severity={badge.severity}
            className={styles.rowBadge}
          />
        )}
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <span className={styles.kicker}>Policy Application</span>
            <h1 className={styles.title}>
              {title}
              <span className={styles.suffix}>{application.suffix}</span>
            </h1>
            {effective && (
              <div className={styles.dates}>
                {String(effective)}
                {expiration ? ` – ${String(expiration)}` : ""}
              </div>
            )}
          </div>
          <Tag
            value={statusLabel(application.status)}
            severity={statusSeverity(application.status)}
            rounded
          />
        </div>

        <nav className={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`${styles.tab} ${tab === t.key ? styles.tabActive : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <div className={styles.content}>
        {tab === "overview" && (
          <div className={styles.inner}>
            {groups.map((group) => {
              const addable = data.addable_sections.includes(group.section);
              return (
                <section key={group.section} className={styles.section}>
                  <h2 className={styles.sectionTitle}>{group.section}</h2>
                  <div className={styles.fields}>
                    {group.fields.map(renderField)}
                    {group.fields.length === 0 && !addable && (
                      <div className={styles.emptySection}>No fields.</div>
                    )}
                    {addable &&
                      (addingSection === group.section ? (
                        <div className={styles.addForm}>
                          <InputText
                            placeholder="Name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                          />
                          <InputText
                            className={styles.input}
                            placeholder="Value"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                          />
                          <Button
                            icon="pi pi-check"
                            rounded
                            size="small"
                            disabled={!newName.trim()}
                            onClick={() => confirmAdd(group.section)}
                          />
                          <Button
                            icon="pi pi-times"
                            rounded
                            text
                            size="small"
                            onClick={() => setAddingSection(null)}
                          />
                        </div>
                      ) : (
                        <button
                          className={styles.addButton}
                          onClick={() => {
                            setAddingSection(group.section);
                            setNewName("");
                            setNewValue("");
                          }}
                        >
                          <i className="pi pi-plus" /> Add{" "}
                          {group.section.replace(/s$/, "").toLowerCase()}
                        </button>
                      ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
        {tab === "forms" && (
          <FormSelectionTab
            applicationId={applicationId ?? ""}
            reportId={application.report_id}
          />
        )}
        {tab === "quote" && (
          <React.Suspense fallback={<ProgressSpinner />}>
            <QuoteTab
              applicationId={applicationId ?? ""}
              reportId={application.report_id}
            />
          </React.Suspense>
        )}
        {tab === "documents" && (
          <DocumentsTab
            applicationId={applicationId ?? ""}
            reportId={application.report_id}
          />
        )}
      </div>

      {dirty && (
        <div className={styles.saveBar}>
          <span className={styles.saveHint}>
            {missingRequired ? (
              <>
                <i className="pi pi-exclamation-circle" /> Required fields blank
              </>
            ) : (
              <>
                <i className="pi pi-pencil" /> Unsaved changes
              </>
            )}
          </span>
          <Button
            label="Discard"
            text
            onClick={() => {
              if (!saved) return;
              setDraft({
                upserts: { ...saved.upserts },
                removals: [...saved.removals],
              });
              setEditingKeys(new Set());
            }}
          />
          <Button
            label="Save changes"
            icon="pi pi-check"
            loading={update.isPending}
            disabled={missingRequired}
            onClick={() =>
              update.mutate({
                overlay: draft,
                notes: application.working_set.notes,
              })
            }
          />
        </div>
      )}
    </div>
  );
};
